import {
  ApiListPracticeProspect,
  ApiTagTypes,
  BaseFolder,
  Folder,
  FolderHierarchy,
  PagePagination,
  PracticeProspect,
} from '../../types';
import { parseProspect } from '../../utils';
import { apiSlice } from '../api.services';

type IncludeFoldersParams = {
  includeFolders?: boolean;
  includeProspects?: boolean;
};

type GetFoldersParams = IncludeFoldersParams & {
  all?: boolean;
  flat?: boolean;
  orgId?: string;
};

type GetFolderByIdParams = IncludeFoldersParams & {
  id: string;
  page?: number;
  perPage?: number;
};

type ApiProspects = {
  pagination: PagePagination;
  data: ApiListPracticeProspect[];
};

type ParsedProspects = {
  pagination: PagePagination;
  data: PracticeProspect[];
};

type BaseGetFolderByIdResponseData = {
  folder: BaseFolder;
  subFolders?: Folder[];
};

type ApiGetFolderByIdResponseData = BaseGetFolderByIdResponseData & {
  prospects?: ApiProspects;
};

type BaseGetFoldersResponseData = {
  folders?: FolderHierarchy[];
  subFolders?: Folder[];
};

type ApiGetFoldersResponseData = BaseGetFoldersResponseData & {
  prospects?: ApiProspects;
};

// Represents the response data as returned from the API for a folder after transformation.
type ParsedGetFoldersResponseData = BaseGetFoldersResponseData & {
  prospects?: ParsedProspects;
};

// Represents the response data as returned from the API for a folder after transformation.
type ParsedGetFolderByIdResponseData = BaseGetFolderByIdResponseData & {
  prospects?: ParsedProspects;
};

type GetFoldersResponse = {
  data: ApiGetFoldersResponseData;
};

type GetFolderByIdResponse = {
  data: ApiGetFolderByIdResponseData;
};

function parseFolderResponse(response: GetFoldersResponse | GetFolderByIdResponse) {
  const data = response.data;
  // If there are no prospects, return the data as is.
  if (!data.prospects) return data;
  // If there are prospects, parse them.
  return {
    ...data,
    prospects: {
      ...data.prospects,
      data: data.prospects.data.map(parseProspect),
    },
  };
}

const enhancedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFolders: builder.query<ParsedGetFoldersResponseData, GetFoldersParams>({
      query: (params) => ({
        url: '/folders',
        params,
      }),
      transformResponse: (response: GetFoldersResponse) =>
        parseFolderResponse(response) as ParsedGetFoldersResponseData,
      providesTags: [ApiTagTypes.FOLDERS],
    }),
    getFolderById: builder.query<ParsedGetFolderByIdResponseData, GetFolderByIdParams>({
      query: ({ id, ...params }) => ({
        url: `/folders/${id}`,
        params,
      }),
      transformResponse: (response: GetFolderByIdResponse) =>
        parseFolderResponse(response) as ParsedGetFolderByIdResponseData,
      providesTags: (_, __, { id }) => [{ type: ApiTagTypes.FOLDERS, id }],
    }),
  }),
});

export const { useGetFoldersQuery, useGetFolderByIdQuery } = enhancedApiSlice;
